import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Input, Select, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import supportAdminService from '../../services/supportAdminService';
import { adminPath } from '../../../shared/config/paths';
import { useTranslation } from '../../../shared/i18n';
import { ok, STATUS_LABEL, STATUS_COLOR, PRIORITY_LABEL, PRIORITY_COLOR, fmtTime } from './common';

const { Text } = Typography;
const FILTER_KEYS = ['status', 'department_id', 'service_type', 'priority', 'assigned', 'user_id', 'q', 'page', 'per_page'];

const Tickets = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();
    const [meta, setMeta] = useState(null);
    const [data, setData] = useState({ items: [], total: 0, counts: {} });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [kw, setKw] = useState(sp.get('q') || '');

    // 过滤条件放在 URL 上：从详情页返回时保持原样。status 缺省=待处理，'all'=全部。
    const f = useMemo(() => {
        const o = {};
        FILTER_KEYS.forEach((k) => { const v = sp.get(k); if (v) o[k] = v; });
        if (!o.status) o.status = 'open';
        return o;
    }, [sp]);
    const setF = (patch) => {
        const next = { ...f, page: undefined, ...patch };
        const p = {};
        Object.entries(next).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p[k] = String(v); });
        setSp(p, { replace: true });
    };

    const load = useCallback(async () => {
        setLoading(true);
        const params = { per_page: 20, ...f };
        if (params.status === 'all') delete params.status;
        const r = await supportAdminService.getTickets(params);
        setLoading(false);
        if (ok(r)) {
            setError('');
            setData({ items: r.data?.items || [], total: r.data?.total || 0, counts: r.data?.counts || {} });
        } else {
            setError(r?.msg || t('Failed to load'));
        }
    }, [f, t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { supportAdminService.getMeta().then((r) => { if (ok(r)) setMeta(r.data); }); }, []);

    const counts = data.counts || {};
    const all = (counts.open || 0) + (counts.answered || 0) + (counts.closed || 0);
    const tab = (key, label, n, color) => ({
        key,
        label: <Space size={6}>{label}<Badge count={n} showZero={false} overflowCount={999} color={color} /></Space>,
    });

    const columns = [
        { title: t('Number'), dataIndex: 'number', width: 130, render: (v) => <Text code>{v}</Text> },
        {
            title: t('Subject'), dataIndex: 'subject', ellipsis: { showTitle: false },
            render: (v, r) => (
                <Tooltip title={v} placement="topLeft">
                    <span>
                        {(r.priority === 'high' || r.priority === 'urgent') && <Tag color={PRIORITY_COLOR[r.priority]}>{t(PRIORITY_LABEL[r.priority])}</Tag>}
                        {v}
                    </span>
                </Tooltip>
            ),
        },
        { title: t('Customer'), dataIndex: 'user_email', width: 180, ellipsis: true, render: (v, r) => v || r.user_name || `#${r.user_id}` },
        {
            title: t('Related service'), dataIndex: 'service_type', width: 180, ellipsis: true,
            render: (v, r) => (v ? <Space size={4}><Tag>{t(r.service_type_label || v)}</Tag><Text>{r.resource_label}</Text></Space> : <Text type="secondary">-</Text>),
        },
        { title: t('Department'), dataIndex: 'department_name', width: 110, render: (v) => v || '-' },
        { title: t('Assignee'), dataIndex: 'assignee_name', width: 110, render: (v) => v || <Text type="secondary">{t('Not assigned')}</Text> },
        { title: t('Status'), dataIndex: 'status', width: 90, render: (v) => <Tag color={STATUS_COLOR[v]}>{t(STATUS_LABEL[v] || v)}</Tag> },
        {
            title: t('Last reply'), dataIndex: 'last_reply_at', width: 140,
            render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <Text type={r.last_reply_by === 'user' ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
                        {r.last_reply_by === 'admin' ? t('Support') : t('Customer')}
                    </Text>
                    <Text style={{ fontSize: 12 }}>{fmtTime(v || r.created_at)}</Text>
                </Space>
            ),
        },
    ];

    const sel = (key, placeholder, options, width = 140) => (
        <Select allowClear style={{ width }} placeholder={placeholder} value={f[key] || undefined}
            onChange={(v) => setF({ [key]: v })} options={options} />
    );

    return (
        <div>
            <PageHeader title={t('Tickets')} extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />} />
            <Tabs activeKey={f.status} onChange={(k) => setF({ status: k })} items={[
                tab('open', t('Pending'), counts.open, '#fa8c16'),
                tab('answered', t('Answered'), counts.answered, '#1677ff'),
                tab('closed', t('Closed'), counts.closed, '#bfbfbf'),
                tab('all', t('All'), all, '#bfbfbf'),
            ]} />
            <Space wrap style={{ marginBottom: 12 }}>
                {sel('department_id', t('Department'), (meta?.departments || []).map((d) => ({ value: String(d.id), label: d.name })))}
                {sel('service_type', t('Service type'), (meta?.service_types || []).map((s) => ({ value: s.type, label: t(s.label) })))}
                {sel('priority', t('Priority'), (meta?.priorities || ['low', 'normal', 'high', 'urgent']).map((p) => ({ value: p, label: t(PRIORITY_LABEL[p] || p) })), 110)}
                <Select style={{ width: 110 }} value={f.assigned || ''} onChange={(v) => setF({ assigned: v })} options={[
                    { value: '', label: t('All') },
                    { value: 'me', label: t('Mine') },
                    { value: 'none', label: t('Not assigned') },
                ]} />
                <Input.Search allowClear style={{ width: 260 }} placeholder={t('Number / subject / email / resource')} value={kw}
                    onChange={(e) => setKw(e.target.value)} onSearch={(v) => setF({ q: v.trim() })} />
                {f.user_id && <Tag closable onClose={() => setF({ user_id: undefined })}>{t('User')} #{f.user_id}</Tag>}
            </Space>
            {error && (
                <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error}
                    action={<Button size="small" onClick={load}>{t('Retry')}</Button>} />
            )}
            <Table size="middle" rowKey="id" loading={loading} dataSource={data.items} columns={columns} sticky
                onRow={(r) => ({ onClick: () => navigate(adminPath(`/support/tickets/${r.id}`)), style: { cursor: 'pointer' } })}
                pagination={{
                    current: Number(f.page) || 1, pageSize: Number(f.per_page) || 20, total: data.total,
                    showSizeChanger: true, pageSizeOptions: [20, 50, 100],
                    showTotal: (n) => t('{{total}} total', { total: n }),
                    onChange: (p, size) => {
                        const ps = Number(f.per_page) || 20;
                        setF(size !== ps
                            ? { page: undefined, per_page: size !== 20 ? size : undefined }
                            : { page: p > 1 ? p : undefined, per_page: f.per_page });
                    },
                }} />
        </div>
    );
};

export default Tickets;
